import { useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import DeleteModal from "../../../../components/DeleteModal";
import {
  useCreateBookingExpenseMutation,
  useDeleteBookingExpenseMutation,
  useGetBookingExpenses,
  useUpdateBookingExpenseMutation,
} from "../../../../../api/fleet-management/booking-expenses/booking-expenses.hooks";
import { formatBookingAmount } from "../utils/booking.utils";

import { MdDelete, MdEdit } from "react-icons/md";

const initialForm = {
  bookingId: "",
  vehicleId: "",
  expenseType: "",
  amount: "",
  description: "",
  expenseDate: "",
};

const normalizeBookingExpenses = (data) => {
  if (Array.isArray(data)) return data;
  return data?.bookingExpenses || data?.expenses || data?.items || [];
};

const formatExpenseDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-PK", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
};

const getVehicleIdValue = (vehicle) => {
  if (!vehicle) return "";
  if (typeof vehicle === "string") return vehicle;
  return vehicle?._id || vehicle?.id || "";
};

const getBookingIdValue = (booking) => {
  if (!booking) return "";
  if (typeof booking === "string") return booking;
  return booking?._id || booking?.id || "";
};

const mapExpenseToForm = (expense, bookingId) => ({
  bookingId,
  vehicleId: getVehicleIdValue(expense?.vehicleId),
  expenseType: expense?.expenseType || "",
  amount:
    expense?.amount !== undefined && expense?.amount !== null
      ? String(expense.amount)
      : "",
  description: expense?.description || "",
  expenseDate: expense?.expenseDate
    ? new Date(expense.expenseDate).toISOString().slice(0, 10)
    : "",
});

const BookingExpensesSection = ({ bookingId, vehicles = [] }) => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState("");
  const [editingExpense, setEditingExpense] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const vehicleOptions = useMemo(() => {
    return vehicles
      .map((vehicle) => {
        const vehicleId = getVehicleIdValue(vehicle?.vehicleId);
        if (!vehicleId) return null;

        const label =
          [vehicle?.snapshot?.make, vehicle?.snapshot?.model].filter(Boolean).join(" ") ||
          [vehicle?.vehicleId?.make, vehicle?.vehicleId?.model].filter(Boolean).join(" ") ||
          "Vehicle";

        return {
          value: vehicleId,
          label: `${label}${vehicle?.vehicleId?.registrationNumber ? ` (${vehicle.vehicleId.registrationNumber})` : ""}`,
        };
      })
      .filter(Boolean);
  }, [vehicles]);

  const { data, isLoading, isError, error } = useGetBookingExpenses(
    bookingId ? { bookingId } : null
  );
  const { mutate: createBookingExpense, isLoading: isCreating } = useCreateBookingExpenseMutation();
  const { mutate: updateBookingExpense, isLoading: isUpdating } = useUpdateBookingExpenseMutation();
  const { mutate: deleteBookingExpense, isLoading: isDeleting } = useDeleteBookingExpenseMutation();

  const expenses = useMemo(() => {
    const normalized = normalizeBookingExpenses(data);
    if (!bookingId) return normalized;
    return normalized.filter((expense) => getBookingIdValue(expense?.bookingId) === bookingId);
  }, [data, bookingId]);

  const openModal = () => {
    setFeedback({ type: "", message: "" });
    setEditingExpense(null);
    setForm({
      ...initialForm,
      bookingId,
      vehicleId: vehicleOptions[0]?.value || "",
      expenseDate: new Date().toISOString().slice(0, 10),
    });
    setShowModal(true);
  };

  const openEditModal = (expense) => {
    setFeedback({ type: "", message: "" });
    setEditingExpense(expense);
    setForm(mapExpenseToForm(expense, bookingId));
    setShowModal(true);
  };

  const isSubmitting = isCreating || isUpdating;

  const closeModal = () => {
    if (isSubmitting) return;
    setShowModal(false);
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFeedback({ type: "", message: "" });

    const payload = {
      bookingId,
      vehicleId: form.vehicleId,
      expenseType: form.expenseType.trim(),
      amount: Number(form.amount),
      description: form.description.trim(),
      expenseDate: form.expenseDate,
    };

    if (
      !payload.bookingId ||
      !payload.vehicleId ||
      !payload.expenseType ||
      !payload.amount ||
      payload.amount <= 0 ||
      !payload.description ||
      !payload.expenseDate
    ) {
      setFeedback({
        type: "warning",
        message: "Please fill in all fields with valid values.",
      });
      return;
    }

    if (editingExpense?._id) {
      updateBookingExpense(
        {
          bookingExpenseId: editingExpense._id,
          bookingExpenseData: payload,
        },
        {
          onSuccess: () => {
            setFeedback({ type: "success", message: "Booking expense updated successfully." });
            setShowModal(false);
            setEditingExpense(null);
            setForm(initialForm);
          },
          onError: (updateError) => {
            setFeedback({
              type: "danger",
              message: updateError?.response?.data?.message || "Failed to update booking expense.",
            });
          },
        }
      );
      return;
    }

    createBookingExpense(payload, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Booking expense created successfully." });
        setShowModal(false);
        setForm(initialForm);
      },
      onError: (createError) => {
        setFeedback({
          type: "danger",
          message: createError?.response?.data?.message || "Failed to create booking expense.",
        });
      },
    });
  };

  const handleDelete = (expenseId) => {
    if (!expenseId || isDeleting) return;

    setSelectedExpenseId(expenseId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setSelectedExpenseId("");
  };

  const handleConfirmDelete = () => {
    if (!selectedExpenseId || isDeleting) return;

    setFeedback({ type: "", message: "" });
    deleteBookingExpense(selectedExpenseId, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Booking expense deleted successfully." });
        closeDeleteModal();
      },
      onError: (deleteError) => {
        setFeedback({
          type: "danger",
          message: deleteError?.response?.data?.message || "Failed to delete booking expense.",
        });
      },
    });
  };

  if (!bookingId) return null;

  return (
    <div className="card">
      <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-0">Booking Expenses</h5>
          <small className="text-muted">Track non-driver expenses for this booking.</small>
        </div>
        <Button variant="primary" size="sm" onClick={openModal} disabled={vehicleOptions.length === 0}>
          Add Expense
        </Button>
      </div>

      <div className="card-body pt-3">
        {vehicleOptions.length === 0 && (
          <Alert variant="warning" className="mb-3">
            No vehicles are assigned to this booking yet.
          </Alert>
        )}

        {feedback.message && (
          <Alert variant={feedback.type} className="mb-3">
            {feedback.message}
          </Alert>
        )}

        {isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        ) : isError ? (
          <Alert variant="danger" className="mb-0">
            Failed to load booking expenses. {error?.response?.data?.message || "Please try again."}
          </Alert>
        ) : expenses.length === 0 ? (
          <div className="text-muted">No expenses found for this booking.</div>
        ) : (
          <div className="table-responsive">
            <Table bordered hover size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Expense Type</th>
                  <th>Amount</th>
                  <th>Description</th>
                  <th>Expense Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => {
                  const vehicleId = getVehicleIdValue(expense?.vehicleId);
                  const vehicle = vehicleOptions.find((item) => item.value === vehicleId);

                  return (
                    <tr key={expense?._id || `${vehicleId}-${expense?.expenseDate || "row"}`}>
                      <td>{vehicle?.label || "-"}</td>
                      <td>{expense?.expenseType || "-"}</td>
                      <td>{formatBookingAmount(expense?.amount)}</td>
                      <td>{expense?.description || "-"}</td>
                      <td>{formatExpenseDate(expense?.expenseDate)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => openEditModal(expense)}
                          disabled={isDeleting || isSubmitting}
                        >
                          <MdEdit size={18} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(expense?._id)}
                          disabled={isDeleting || isSubmitting || !expense?._id}
                        >
                          <MdDelete size={18} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton={!isSubmitting}>
          <Modal.Title>{editingExpense ? "Edit Booking Expense" : "Add Booking Expense"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Vehicle</Form.Label>
                  <Form.Select
                    value={form.vehicleId}
                    onChange={(e) => updateField("vehicleId", e.target.value)}
                    disabled={isSubmitting || vehicleOptions.length === 0}
                  >
                    <option value="">Select Vehicle</option>
                    {vehicleOptions.map((vehicle) => (
                      <option key={vehicle.value} value={vehicle.value}>
                        {vehicle.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Expense Type</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.expenseType}
                    onChange={(e) => updateField("expenseType", e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Expense Type (e.g., Fuel, Toll, Parking)"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={form.amount}
                    onChange={(e) => updateField("amount", e.target.value)}
                    disabled={isSubmitting}
                    placeholder="0"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.description}
                    onChange={(e) => updateField("description", e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Enter expense details"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Expense Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.expenseDate}
                    onChange={(e) => updateField("expenseDate", e.target.value)}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={closeModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting || vehicleOptions.length === 0}>
              {isSubmitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                editingExpense ? "Update Expense" : "Save Expense"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Booking Expense"
        message="Are you sure you want to delete this booking expense? This action cannot be undone."
        confirmText="Delete Expense"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default BookingExpensesSection;